// F5 review + blind round 3: screens load as their own files. After a deploy, a page opened before it asks for file
// names that no longer exist, and the import fails (Pages answers a missing /assets file with 404 since 10/15; before
// that it sent index.html, which the browser kept for a year). Recovery, in order:
//   1. wait 2 s and try the import once more (a deploy that is still spreading across the edge settles in seconds);
//   2. reload the page, which fetches the new index.html and the new names. At most 2 automatic reloads per 2 minutes,
//      so a file that is really broken can never cause a reload loop;
//   3. otherwise the error reaches the app's error screen (ui/Recovery.tsx), which has Reload and Go to Browse.
// Offline, it never reloads (a reload would only show the browser's error page).
const KEY = 'mg_chunk_reload'
const WINDOW_MS = 120_000
const MAX_RELOADS = 2
export const RETRY_DELAY_MS = 2_000

type Env = { now: () => number; wait: (ms: number) => Promise<void>; reload: () => void; online: () => boolean; store: Pick<Storage, 'getItem' | 'setItem'> | null }

const defaultEnv = (): Env => ({
  now: () => Date.now(),
  wait: (ms) => new Promise((r) => setTimeout(r, ms)),
  reload: () => location.reload(),
  online: () => (globalThis as { navigator?: { onLine?: boolean } }).navigator?.onLine !== false,
  store: (() => { try { return sessionStorage } catch { return null } })(),
})

/** True for the errors a missing or stale build file produces (Chrome, Safari, Firefox, Vite's preload helper). */
export function isChunkError(e: unknown): boolean {
  const m = String((e as { message?: unknown })?.message ?? e)
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|module script/i.test(m)
}

/** Records a reload in the session and says whether one is allowed now. */
export function takeReload(env: Pick<Env, 'now' | 'store'>): boolean {
  if (!env.store) return false // no session storage: we could not stop a loop, so never reload
  try {
    const now = env.now()
    const recent = String(env.store.getItem(KEY) ?? '').split(',').map(Number).filter((t) => t && now - t < WINDOW_MS)
    if (recent.length >= MAX_RELOADS) return false
    env.store.setItem(KEY, [...recent, now].join(','))
    return true
  } catch { return false }
}

export function reloadOnce<T>(load: () => Promise<T>, envIn?: Partial<Env>): () => Promise<T> {
  return () => {
    const env = { ...defaultEnv(), ...envIn }
    return load().catch(async (e: unknown) => {
      if (!isChunkError(e)) throw e // a bug inside the screen: retrying or reloading would not help
      if (!env.online()) throw e
      await env.wait(RETRY_DELAY_MS)
      try { return await load() } catch { /* still missing: reload below */ }
      if (!env.online() || !takeReload(env)) throw e
      env.reload()
      return new Promise<T>(() => {}) // the page is going away
    })
  }
}
